import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface ConferenceRoom {
    id: number;
    period_id: number;
    conference_id: number;
    name: string;
    capacity?: number | null;
    attendances_count?: number;
    description?: string | null;
    date?: string | null;
    moderator_id?: number | null;
    moderator?: { name: string; email: string; } | null;
    created_at?: string;
    updated_at?: string;
}

export const useAdminConferenceRooms = (conference_id?: number | string) => {
    return useQuery({
        queryKey: ["admin-conference-rooms", conference_id],
        queryFn: async () => {
            const params: any = {};
            if (conference_id) params.conference_id = conference_id;
            
            const { data } = await axiosClient.get("/private/conference-rooms", { params });
            return data.data as ConferenceRoom[];
        },
    });
};

export const useCreateConferenceRoom = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: Partial<ConferenceRoom>) => {
            const { data } = await axiosClient.post("/private/conference-rooms", payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-conference-rooms"] });
        },
    });
};

export const useUpdateConferenceRoom = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: Partial<ConferenceRoom> }) => {
            const { data } = await axiosClient.put(`/private/conference-rooms/${id}`, payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-conference-rooms"] });
        },
    });
};

export const useDeleteConferenceRoom = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`/private/conference-rooms/${id}`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-conference-rooms"] });
        },
    });
};

export const useImportConferenceRooms = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (formData: FormData) => {
            const { data } = await axiosClient.post("/private/conference-rooms/import", formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-conference-rooms"] });
        },
    });
};


export const useSetModerator = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: { name: string; email: string } }) => {
            const { data } = await axiosClient.post(`/private/conference-rooms/${id}/moderator`, payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-conference-rooms"] });
        },
    });
};